import { useEffect, useState } from "react";
import { Clock, PlayCircle, CheckCircle, TrendingUp, TrendingDown, Database, Trash2 } from "lucide-react";

interface RunInfo {
  run_id: string;
  timestamp: string;
  dataset_version: string;
  agent_name?: string;
  agent_path?: string;
  model?: string;
  total_questions: number;
  success_rate?: number;
  success_count?: number;
  mean_accuracy: number;
  sql_success_rate_pct?: number;
  is_current: boolean;
}

interface LiveRunInfo {
  run_id: string;
  agent_name?: string;
  agent_path?: string;
  model?: string;
  dataset_version?: string;
  total_questions?: number;
  mean_accuracy?: number;
}

interface RunHistoryProps {
  onSelectRun: (runId: string) => void;
  liveRunId?: string | null;
  liveRunInfo?: LiveRunInfo | null;
  currentRunId?: string;
  viewMode?: 'live' | 'historical';
  onBackToLive?: () => void;
}

export function RunHistory({ onSelectRun, liveRunId, liveRunInfo, currentRunId, viewMode, onBackToLive }: RunHistoryProps) {
  const [runs, setRuns] = useState<RunInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);

  // Fetch runs function
  const fetchRuns = () => {
    fetch('/api/evaluation/runs')
      .then(response => response.json())
      .then(data => {
        setRuns(data.runs || []);
        setLoading(false);
      })
      .catch(error => {
        console.error("Failed to fetch runs:", error);
        setLoading(false);
      });
  };

  // Delete a run
  const handleDelete = async (runId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering onSelectRun

    if (!confirm(`Delete run ${runId}? This cannot be undone.`)) {
      return;
    }

    setDeleting(runId);
    try {
      const response = await fetch(`/api/evaluation/runs/${runId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        // Remove from local state
        setRuns(prev => prev.filter(r => r.run_id !== runId));
        console.log(`🗑️ Deleted run ${runId}`);
      } else {
        const error = await response.json();
        alert(`Failed to delete: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error("Failed to delete run:", error);
      alert('Failed to delete run');
    } finally {
      setDeleting(null);
    }
  };

  // Fetch historical runs list on mount
  useEffect(() => {
    fetchRuns();
  }, []);

  // Listen for evaluation-complete events to refresh runs list
  useEffect(() => {
    const handleEvaluationComplete = () => {
      console.log('📋 Refreshing runs list after evaluation complete');
      fetchRuns();
    };
    window.addEventListener('evaluation-complete', handleEvaluationComplete);
    return () => window.removeEventListener('evaluation-complete', handleEvaluationComplete);
  }, []);

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
           ' ' + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  const formatAgentName = (agentName?: string, agentPath?: string) => {
    if (agentName) return agentName;
    if (agentPath) {
      // Extract agent name from path like "agents/bigquery_agent_v1/agent.py"
      const match = agentPath.match(/agents\/([^/]+)/);
      return match ? match[1] : agentPath;
    }
    return 'Unknown';
  };

  const formatModel = (model?: string) => {
    if (!model) return 'gemini-2.5-flash';
    // Shorten model name for display
    return model.replace('gemini-', '').replace('-preview', '');
  };

  const getScoreColor = (score: number) => {
    if (score >= 4.5) return "text-success";
    if (score >= 3.5) return "text-warning";
    return "text-destructive";
  };

  const getScoreIcon = (score: number) => {
    if (score >= 4.0) return <TrendingUp className="h-4 w-4" />;
    return <TrendingDown className="h-4 w-4" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-sm text-muted-foreground">Loading runs...</div>
      </div>
    );
  }

  // Filter out the live run from historical runs
  const historicalRuns = runs.filter(r => r.run_id !== liveRunId);

  return (
    <div className="flex flex-col h-full">
      <div className="mb-4 flex items-center justify-between flex-shrink-0">
        <h3 className="text-lg font-bold bg-gradient-to-r from-primary to-orange-400 bg-clip-text text-transparent">
          Run History
        </h3>
        <Database className="h-5 w-5 text-primary" />
      </div>

      {/* Back to Live Run Button */}
      {liveRunId && viewMode === 'historical' && onBackToLive && (
        <div className="mb-4 flex-shrink-0">
          <button
            onClick={onBackToLive}
            className="w-full p-3 rounded-lg border-2 border-success bg-success/10 hover:bg-success/20 transition-all flex items-center justify-center gap-2 group"
          >
            <div className="h-2 w-2 rounded-full bg-success animate-pulse" />
            <span className="font-semibold text-success">Back to Live Run</span>
            <PlayCircle className="h-4 w-4 text-success group-hover:scale-110 transition-transform" />
          </button>
        </div>
      )}

      <div className="flex-1 overflow-y-auto space-y-3 pr-2">
        {/* Live Running Evaluation */}
        {liveRunId && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2 text-xs text-muted-foreground uppercase tracking-wide">
              <div className="h-2 w-2 rounded-full bg-success animate-pulse" />
              Live Evaluation
            </div>
            <div
              onClick={() => onSelectRun(liveRunId)}
              className={`p-3 rounded-lg border backdrop-blur-sm cursor-pointer transition-all hover:scale-[1.02] ${
                viewMode === 'live'
                  ? 'border-success bg-success/10 shadow-lg shadow-success/20'
                  : 'border-primary/30 bg-card/50 hover:border-primary/50'
              }`}
            >
              {/* Top row: Agent name and running indicator */}
              <div className="flex items-center justify-between mb-2">
                <div className="font-semibold text-sm text-foreground truncate flex-1 mr-2">
                  {formatAgentName(liveRunInfo?.agent_name, liveRunInfo?.agent_path)}
                </div>
                <div className="flex items-center gap-1.5 text-success">
                  <div className="h-2 w-2 rounded-full bg-success animate-pulse" />
                  <span className="text-xs font-medium">Running</span>
                </div>
              </div>

              {/* Middle row: Model and Dataset */}
              <div className="flex items-center gap-2 text-xs mb-2">
                <span className="px-1.5 py-0.5 rounded bg-success/20 text-success font-medium">
                  {formatModel(liveRunInfo?.model)}
                </span>
                <span className="px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                  {liveRunInfo?.dataset_version || 'v1.0'}
                </span>
                {liveRunInfo?.total_questions && (
                  <span className="text-muted-foreground">
                    {liveRunInfo.total_questions}q
                  </span>
                )}
              </div>

              {/* Bottom row: Score (if available) */}
              {liveRunInfo?.mean_accuracy !== undefined && liveRunInfo.mean_accuracy > 0 && (
                <div className={`flex items-center gap-1 text-sm font-bold ${getScoreColor(liveRunInfo.mean_accuracy)}`}>
                  {getScoreIcon(liveRunInfo.mean_accuracy)}
                  <span>{liveRunInfo.mean_accuracy.toFixed(2)}</span>
                  <span className="text-xs font-normal text-muted-foreground ml-1">avg</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Historical Runs */}
        {historicalRuns.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2 text-xs text-muted-foreground uppercase tracking-wide">
              <CheckCircle className="h-3 w-3" />
              Historical Runs
            </div>
            <div className="space-y-2">
              {historicalRuns.map((run) => (
                <div
                  key={run.run_id}
                  onClick={() => {
                    console.log("🖱️ Clicked historical run:", run.run_id);
                    onSelectRun(run.run_id);
                  }}
                  className={`p-3 rounded-lg border backdrop-blur-sm cursor-pointer transition-all hover:scale-[1.01] group relative ${
                    viewMode === 'historical' && currentRunId === run.run_id
                      ? 'border-primary bg-primary/10 shadow-md shadow-primary/10'
                      : 'border-border/50 bg-card/30 hover:border-primary/30 hover:bg-card/50'
                  }`}
                >
                  {/* Delete button - shows on hover */}
                  <button
                    onClick={(e) => handleDelete(run.run_id, e)}
                    disabled={deleting === run.run_id}
                    className="absolute top-2 right-2 p-1.5 rounded-md opacity-0 group-hover:opacity-100 transition-opacity bg-destructive/10 hover:bg-destructive/20 text-destructive disabled:opacity-50"
                    title="Delete run"
                  >
                    {deleting === run.run_id ? (
                      <div className="h-3.5 w-3.5 border-2 border-destructive border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Trash2 className="h-3.5 w-3.5" />
                    )}
                  </button>

                  {/* Top row: Agent name and Score */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-semibold text-sm text-foreground truncate flex-1 mr-8">
                      {formatAgentName(run.agent_name, run.agent_path)}
                    </div>
                    <div className={`flex items-center gap-1 text-lg font-bold ${getScoreColor(run.mean_accuracy)}`}>
                      {getScoreIcon(run.mean_accuracy)}
                      <span>{(run.mean_accuracy || 0).toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Middle row: Model and Dataset */}
                  <div className="flex items-center gap-2 text-xs mb-2">
                    <span className="px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium">
                      {formatModel(run.model)}
                    </span>
                    <span className="px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                      {run.dataset_version}
                    </span>
                    <span className="text-muted-foreground">
                      {run.total_questions}q
                    </span>
                  </div>

                  {/* Bottom row: Date */}
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    <span>{formatTimestamp(run.timestamp)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {runs.length === 0 && !liveRunId && (
          <div className="flex flex-col items-center justify-center h-full text-center p-6">
            <Database className="h-12 w-12 text-muted-foreground/30 mb-3" />
            <p className="text-sm text-muted-foreground">No evaluation runs found</p>
            <p className="text-xs text-muted-foreground/70 mt-1">
              Start an evaluation to see history
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
